import { IS_SERVER } from '@/renderer/utils/ssr';

/**
 * Shared home-screen app-icon badge aggregator (issues #1347, #1212).
 *
 * The badge (via the Badging API `navigator.setAppBadge` / `clearAppBadge`) is a
 * single OS-level number, but we have multiple independent unread sources feeding
 * it — support-chat unreads and release-center unreads. If each source wrote the
 * badge directly they'd clobber each other (whichever updated last would win),
 * which is why support-chat notifications were getting masked by the release
 * center count and going unseen.
 *
 * Every unread source reports its current count here keyed by a stable id. The
 * badge is then set to the SUM of all sources, and only cleared once every source
 * is at zero. Those same sources are the sidebar unread counters (Nav.tsx), so
 * the app-icon badge is the SUM OF THE SIDEBAR BADGES from ONE source of truth,
 * and it persists exactly as long as the sidebar badges do — it drops only when a
 * source is read (its count goes to 0), never merely because the app was opened
 * or focused. The Badging API is feature-detected and PWA/installed-only, so this
 * is a no-op (never throws) on browsers/contexts that don't support it.
 *
 * Opening/focusing the app must NOT clear the badge on its own (#1212): "read" is
 * a source going to zero (navigating to the release center, opening a chat
 * thread), not "the window gained focus". So the focus/visibility paths here and
 * in sw/register.ts RECONCILE the badge to the live source sum rather than
 * clearing it — an open with items still unread keeps the badge at the sum.
 */

const counts = new Map<string, number>();

/**
 * The app-icon badge is a SINGLE per-app value that ANY same-origin context can
 * write — including the notifications service worker (sw/sw.ts), which sets it
 * from a background Web Push so a CLOSED or BACKGROUNDED PWA still badges when a
 * support-chat message arrives (#1694/#1719). A page that is NOT the foreground
 * window must therefore never write the badge: if it did, a backgrounded tab or a
 * still-open-but-unfocused PWA window would clobber the SW's push-set badge back
 * to its own (usually zero) live unread total — the exact "the notification
 * arrives but no badge appears" bug.
 *
 * "Foreground" here must mean the SAME thing the SW's push guard means by it, or
 * the two authorities fight: the SW skips badging only when a client is `focused`
 * (matchAll → `focused === true`), so the page may own the badge only when it is
 * that focused client — i.e. visible AND `document.hasFocus()`. Keying ownership
 * on visibility ALONE (#1719) left a gap: a visible-but-unfocused PWA whose live
 * unread is 0 (because the message was delivered by the SW push, not a live
 * websocket event) would clear the badge the SW had just set (#1738). So the page
 * owns the badge only while it is the focused window (reconciling to the true
 * unread total then, including clearing once everything is read), and leaves it to
 * the service worker otherwise.
 */
function pageOwnsBadge(): boolean {
  return (
    !IS_SERVER &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'visible' &&
    typeof document.hasFocus === 'function' &&
    document.hasFocus()
  );
}

function applyBadge() {
  // Badging API only exists in secure, installed/PWA contexts. Guard it so we
  // never throw where it's unsupported (plain browser tabs, SSR, etc.).
  if (IS_SERVER || typeof navigator === 'undefined' || !('setAppBadge' in navigator)) {
    return;
  }

  // Don't let a hidden page fight the service worker's push-set badge (#1719).
  if (!pageOwnsBadge()) {
    return;
  }

  let total = 0;
  for (const value of counts.values()) {
    total += value;
  }

  if (total <= 0) {
    navigator.clearAppBadge?.();
  } else {
    navigator.setAppBadge?.(total);
  }
}

/**
 * In-page subscribers to the source map (the mobile tab bar's Chat badge).
 *
 * The OS badge is only one of the things these counts feed. A second piece of
 * chrome that has to show the SAME unread number the sidebar shows — the Chat
 * tab in components/Nav/MobileTabBar.tsx — reads it from here rather than
 * mounting a second WebSocketUnreadCounter for the same dataPath. A second
 * counter would open its own websocket subscription and its own seed/reconcile
 * fetch, and (because both would register under the same `dataPath` badge key)
 * the two would fight over this map and over the OS badge. So the sidebar
 * counter stays the single writer and everything else observes it: one count,
 * one source of truth, no extra release-time traffic (#1801).
 */
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribe to any change in the source map. Returns an unsubscribe function. */
export function subscribeBadgeSources(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current count for one source, or 0 when nothing has reported under it. */
export function getBadgeSource(key: string): number {
  return counts.get(key) ?? 0;
}

/** Report the current unread count for a given source and refresh the badge. */
export function setBadgeSource(key: string, count: number) {
  counts.set(key, Math.max(count, 0));
  applyBadge();
  notify();
}

/** Remove a source (e.g. on unmount) and refresh the badge from the remainder. */
export function clearBadgeSource(key: string) {
  if (counts.delete(key)) {
    applyBadge();
    notify();
  }
}

/**
 * Recompute the OS badge from the live source sum. This is what "opening the app"
 * must do (#1212): reconcile to the true sidebar total — which persists the badge
 * when items are still unread and clears it only once every source is at zero —
 * instead of blanket-clearing on focus. Respects the SW-vs-page ownership guard
 * (a hidden/unfocused page is a no-op, so it never clobbers the SW push badge).
 * Feature-detected + never throws where badging is unsupported.
 */
export function reconcileBadge() {
  applyBadge();
}

// Reconcile when this page becomes visible or focused again — the moment it takes
// badge ownership back from the service worker. Without this, a page that was
// hidden or unfocused (its live counts kept, the SW owning the badge) wouldn't
// refresh the badge to its own totals until the next count change; and a plain
// focus must reconcile to the sum, never clear (#1212). Both handlers route
// through applyBadge, which is gated on the page actually owning the badge, so a
// background/unfocused fire is a harmless no-op. Registered once at module load;
// no-op on the server / where there's no document.
if (!IS_SERVER && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      applyBadge();
    }
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => applyBadge());
  }
}
